import { DateFormatter, getMaxDateForAge } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { MIN_AGE_REQUIRED } from '@repo/toolkit/min-age-required'
import { TFunction } from 'i18next'
import { z, ZodType } from 'zod'

import { GenderEnum } from '../../../lib/types/enums/common/gender.enum'
import { TaughtLevelEnum } from '../../../lib/types/enums/common/taught-level.enum'
import { LanguageEnum } from '../../../lib/types/enums/languages/language.enum'
import { TeflInput } from '../../../lib/types/models/tefl/tefl-input'
import { TeflDiplomaInput } from '../../../lib/types/models/tefl-diploma/tefl-diploma-input'
import { TeflLecComment } from '../../../lib/types/models/tefl-lec-comment/tefl-lec-comment'
import { TeflLecCommentInput } from '../../../lib/types/models/tefl-lec-comment/tefl-lec-input'
import { TeflOlComment } from '../../../lib/types/models/tefl-ol-comment/tefl-ol-comment'
import { TeflOlCommentInput } from '../../../lib/types/models/tefl-ol-comment/tefl-ol-comment-input'
import { createDateStringSchema } from '../../../lib/utils/validations/date.validation'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { FILE_VALIDATION } from '../../../lib/utils/validations/file.validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'

export const teflDiplomaFormSchema: ZodType<TeflDiplomaInput> = z.object({
  name: createStringSchema('Diploma name', { language: 'en' }),
  yearOfObtention: createNumberSchema('Year of obtention', {
    isInteger: true,
    minimumValue: 1900,
    maximumValue: new Date().getFullYear(),
    language: 'en'
  }),
  file: FILE_VALIDATION()
})

const baseTeflFormSchema: (t: TFunction<AvailableLanguages, undefined>) => ZodType<TeflInput> = (
  t
) =>
  z.object({
    gender: createEnumSchema({ enumType: GenderEnum, fieldName: 'gender' }),
    firstName: createStringSchema('first name'),
    lastName: createStringSchema('last name'),
    birthDate: createDateStringSchema({
      inputName: 'date of birth',
      maxDate: DateFormatter.toDateFormatted(getMaxDateForAge(MIN_AGE_REQUIRED)),
      errorMessage: `Âge minimum requis: ${MIN_AGE_REQUIRED} ans`
    }),
    email: createStringSchema('email', { isEmail: true }),
    phoneNumber: PHONE_NUMBER_SCHEMA(t),
    address1: createStringSchema('address'),
    address2: createStringSchema('address 2', { nullish: true }),
    city: createStringSchema('city'),
    postalCode: createStringSchema('postal code'),
    country: createStringSchema('country'),
    motherTongue: createEnumSchema({
      enumType: LanguageEnum,
      fieldName: 'mother tongue'
    }),
    nationality: createStringSchema('nationalité'),
    firstYearOfTeaching: createNumberSchema('First year of teaching', {
      isInteger: true,
      minimumValue: 1900,
      maximumValue: new Date().getFullYear()
    }),
    mostLevelTaught: createEnumSchema({
      enumType: TaughtLevelEnum,
      fieldName: 'most taught level'
    }),
    ableToTeachInAnotherArea: z.boolean({
      required_error: 'Veuillez choisir une option'
    }),
    drivingLicense: z.boolean({
      required_error: 'Veuillez choisir une option'
    }),
    car: z.boolean({
      required_error: 'Veuillez choisir une option'
    }),
    comments: createStringSchema('comments', { nullish: true }),
    diplomas: z.array(teflDiplomaFormSchema),
    documents: FILE_VALIDATION().array().min(1, 'Veuillez ajouter au moins un document')
  })

export const teflFormSchema: (
  t: TFunction<AvailableLanguages, undefined>
) => ZodType<TeflInput> = (t) =>
  baseTeflFormSchema(t).and(
    z.union([
      z
        .object({ ableToTeachInAnotherArea: z.literal(true) })
        .merge(z.object({ fieldOfExpertise: createStringSchema('field of expertise') })),
      z.object({ ableToTeachInAnotherArea: z.literal(false) }).merge(z.object({}))
    ])
  )

export const TEFL_LEC_COMMENT_SCHEMA: ZodType<Omit<TeflLecCommentInput, 'updatedAt'>> = z.object({
  comment: createStringSchema('Commentaire'),
  sharedToOl: z.boolean({ required_error: "Le commentaire doit être partagé à l'OL ou non" }),
  isVisibleForTheYear: z.boolean({
    required_error: "Le commentaire doit être visible pour l'année ou non"
  })
})

export const TEFL_OL_COMMENT_SCHEMA: ZodType<Omit<TeflOlCommentInput, 'updatedAt'>> = z.object({
  comment: createStringSchema('Comment')
})

export const mapTeflLecCommentToInput = (data: TeflLecComment): TeflLecCommentInput => {
  return {
    comment: data.comment,
    sharedToOl: data.sharedToOl,
    isVisibleForTheYear: data.isVisibleForTheYear,
    updatedAt: data.updatedAt
  }
}

export const mapTeflOlCommentToInput = (data: TeflOlComment): TeflOlCommentInput => {
  return {
    comment: data.comment,
    updatedAt: data.updatedAt
  }
}
